import { useCallback, useEffect, useRef, useState } from 'react'
import { Combobox, ComboboxOption } from '@/ui'
import { UserWithAdminInfo } from '@/reducers/admin/v2/allUsersReducerV2'
import { SearchType, searchUsers } from '@/actions/admin/v2/adminUsersActions'
import { serializeOrderBy } from '@/utils/sortHelpers'
import { useTimeoutRef } from '@/utils/sharedHooks'

interface UserSearchDropdownProps {
  value: number | undefined
  onChange: (userId: number | undefined) => void
  placeholder?: string
  modal?: boolean
}

const PAGE_SIZE = 25
const DEBOUNCE_MS = 300

export const UserSearchDropdown = ({
  value,
  onChange,
  placeholder = 'Select user...',
  modal = false,
}: UserSearchDropdownProps) => {
  const [userList, setUserList] = useState<UserWithAdminInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [currentSkip, setCurrentSkip] = useState(0)
  const [inputValue, setInputValue] = useState('')

  const debounceRef = useTimeoutRef()
  const userListRef = useRef<UserWithAdminInfo[]>([])
  const requestIdRef = useRef(0)

  // Keep ref in sync with state
  useEffect(() => {
    userListRef.current = userList
  }, [userList])

  const fetchUsers = useCallback(async (query: string, skip: number) => {
    if (!query.trim()) {
      setUserList([])
      setHasMore(false)
      setCurrentSkip(0)
      return
    }
    const currentRequestId = ++requestIdRef.current
    setIsLoading(true)
    try {
      const isId = /^\d+$/.test(query)
      const users = await searchUsers(
        {
          skip,
          take: PAGE_SIZE,
        },
        isId ? SearchType.ById : SearchType.BySearchString,
        query,
        undefined,
        serializeOrderBy([
          { column: 'firstName', direction: 'ascending' },
          { column: 'lastName', direction: 'ascending' },
        ])
          .map((order) => `&orderBy=${order}`)
          .join('')
      )

      // Only update state if this is still the latest request
      if (currentRequestId !== requestIdRef.current) return

      const previousList = skip > 0 ? userListRef.current : []
      const newList = [...previousList, ...users.data]
      setUserList(newList)
      setHasMore(users.pagination.totalItems > newList.length)
      setCurrentSkip(skip)
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  // Debounced search on input change
  const handleInputChange = useCallback(
    (text: string) => {
      setInputValue(text)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (text.trim()) {
        debounceRef.current = setTimeout(() => {
          fetchUsers(text, 0)
        }, DEBOUNCE_MS)
      } else {
        setUserList([])
      }
    },
    [debounceRef, fetchUsers]
  )

  const handleScrollEnd = useCallback(() => {
    if (!isLoading && hasMore && inputValue.trim()) {
      fetchUsers(inputValue, currentSkip + PAGE_SIZE)
    }
  }, [fetchUsers, isLoading, hasMore, currentSkip, inputValue])

  const handleValueChange = useCallback(
    (val: string | null) => {
      if (val) {
        onChange(Number(val))
      } else {
        onChange(undefined)
      }
    },
    [onChange]
  )

  // Build options from user list
  const options: ComboboxOption[] = userList.map((user) => ({
    value: String(user.id),
    label: `${user.id}: ${user.firstName} ${user.lastName}`,
  }))

  const displayValue = value ? String(value) : undefined

  return (
    <Combobox
      options={options}
      value={displayValue}
      onValueChange={handleValueChange}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      shouldFilter={false}
      onScrollEnd={handleScrollEnd}
      isLoading={isLoading}
      searchPlaceholder="Search by name, email, or ID..."
      emptyMessage={inputValue ? 'No users found.' : 'Type to search users...'}
      widthClassName="w-[320px]"
      buttonWidthClassName="min-w-[200px]"
      modal={modal}
    />
  )
}
